import { useEffect, useRef, useState } from "react";
import { MODE_MAX_DIFFICULTY, type ModeConfig } from "../state/modeConfig";
import type { usePlayerState } from "../hooks/usePlayerState";
import { useGameSession } from "../session/useGameSession";
import {
  adaptiveN,
  generateNBackSequence,
  scoreNBack,
  type NBackScore,
} from "../logic/nback";
import { TopBar } from "../components/TopBar";
import { Instructions } from "../screens/Instructions";
import { Results } from "../screens/Results";
import { useTranslation } from "../i18n/useTranslation";

type SubPhase = "countdown" | "stimulus" | "gap" | "summary";

const STIM_COUNT = 20;
// 2.0s stimulus, matching desktop's show_time (and iOS stimShowTime).
const STIM_SHOW_MS = 2000;
const GAP_MS = 500;

interface NBackProps {
  config: ModeConfig;
  player: ReturnType<typeof usePlayerState>;
  onExit: () => void;
}

export function NBack({ config, player, onExit }: NBackProps) {
  const game = useGameSession(config.modeId, player);
  const { session } = game;
  const { t } = useTranslation();

  const [sequence, setSequence] = useState<string[]>([]);
  const [responses, setResponses] = useState<boolean[]>([]);
  const [stimIndex, setStimIndex] = useState(0);
  const [subPhase, setSubPhase] = useState<SubPhase>("countdown");
  const [countdown, setCountdown] = useState(3);
  const respondedRef = useRef(false);
  const [lastResult, setLastResult] = useState<NBackScore | null>(null);

  const lastRoundRef = useRef(-1);
  useEffect(() => {
    if (session.phase.kind !== "playing") return;
    if (lastRoundRef.current === session.roundNum) return;
    lastRoundRef.current = session.roundNum;
    startRound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.phase, session.roundNum]);

  function startRound(): void {
    const n = Math.max(1, session.difficulty);
    setSequence(generateNBackSequence(STIM_COUNT, n));
    setResponses(Array(STIM_COUNT).fill(false));
    setStimIndex(0);
    setSubPhase("countdown");
    setCountdown(3);
    setLastResult(null);
    respondedRef.current = false;
  }

  useEffect(() => {
    if (subPhase !== "countdown") return;
    if (countdown <= 0) {
      setSubPhase("stimulus");
      respondedRef.current = false;
      return;
    }
    const id = window.setTimeout(() => setCountdown((c) => c - 1), 500);
    return () => window.clearTimeout(id);
  }, [subPhase, countdown]);

  useEffect(() => {
    if (subPhase !== "stimulus") return;
    if (stimIndex >= sequence.length) return;
    const id = window.setTimeout(() => setSubPhase("gap"), STIM_SHOW_MS);
    return () => window.clearTimeout(id);
  }, [subPhase, stimIndex, sequence]);

  useEffect(() => {
    if (subPhase !== "gap") return;
    const id = window.setTimeout(() => {
      const next = stimIndex + 1;
      if (next >= sequence.length) {
        const score = scoreNBack(sequence, Math.max(1, session.difficulty), responses);
        setLastResult(score);
        setSubPhase("summary");
      } else {
        setStimIndex(next);
        respondedRef.current = false;
        setSubPhase("stimulus");
      }
    }, GAP_MS);
    return () => window.clearTimeout(id);
  }, [subPhase, stimIndex, sequence, responses, session.difficulty]);

  function handleMatch(): void {
    if (subPhase !== "stimulus" || respondedRef.current) return;
    if (stimIndex >= sequence.length) return;
    respondedRef.current = true;
    setResponses((r) => {
      const copy = [...r];
      copy[stimIndex] = true;
      return copy;
    });
  }

  function continueFromSummary(): void {
    if (!lastResult) return;
    // Adaptive N: adjust difficulty for the next round and persist it to
    // player state every round (mirrors desktop/iOS) so N-Back progression
    // carries across sessions, not just within one.
    const maxN = MODE_MAX_DIFFICULTY[config.modeId] ?? 8;
    const newN = adaptiveN(Math.max(1, session.difficulty), lastResult.accuracyPct, maxN);
    session.difficulty = newN;
    session.playerState.modeDifficulty[config.modeId] = newN;
    game.submitAnswer(lastResult.accuracyPct >= 70);
  }

  if (session.phase.kind === "instructions") {
    const n = Math.max(1, session.difficulty);
    return (
      <Instructions
        config={config}
        difficulty={n}
        highScore={session.playerState.modeHighScores[config.modeId] ?? 0}
        instructions={[
          t("nback.instr.appear", { n }),
          t("nback.instr.firstN"),
          t("nback.instr.threshold"),
        ]}
        difficultyDescription={t("nback.diffDesc", { n, count: STIM_COUNT })}
        onStart={game.startPlaying}
        onBack={onExit}
        onDifficultyChange={game.setDifficulty}
      />
    );
  }
  if (session.phase.kind === "results") {
    return (
      <Results
        modeName={t(`mode.${config.modeId}.name`)}
        result={session.sessionResult}
        level={session.playerState.level}
        xpProgressFrac={session.playerState.xpProgressFrac}
        onContinue={onExit}
      />
    );
  }

  const progress = sequence.length > 0 ? stimIndex / sequence.length : 0;
  const currentLetter = subPhase === "stimulus" ? sequence[stimIndex] : null;
  const n = Math.max(1, session.difficulty);

  return (
    <main className="min-h-screen flex flex-col">
      <TopBar
        modeName={t("nback.modeTitle", { n })}
        score={session.score}
        streak={session.streak.current}
        roundNumber={Math.min(session.roundNum + 1, session.maxRounds)}
        totalRounds={session.maxRounds}
      />
      <div className="h-1 bg-bg-3 mx-5 mt-3 rounded-full overflow-hidden">
        <div
          className="h-full bg-pink transition-[width] duration-100"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      <section className="flex-1 flex flex-col items-center justify-center px-6 gap-8">
        {subPhase === "countdown" && (
          <div className="flex flex-col items-center gap-3 select-none">
            <span className="text-pink text-xl font-medium">{t("common.getReady")}</span>
            <span className="text-7xl font-bold text-fg-0">
              {Math.max(1, countdown)}
            </span>
          </div>
        )}
        {subPhase === "stimulus" && (
          <>
            <div className="w-44 h-44 rounded-full border-4 border-pink flex items-center justify-center">
              <span className="text-7xl font-bold font-mono text-fg-0">
                {currentLetter}
              </span>
            </div>
            <button
              type="button"
              onClick={handleMatch}
              disabled={respondedRef.current}
              className={[
                "px-10 py-3.5 rounded-full text-base font-bold transition-all active:scale-95",
                respondedRef.current
                  ? "bg-green text-bg-1"
                  : "bg-pink text-bg-1 shadow-[0_0_30px_color-mix(in_oklab,var(--color-pink)_50%,transparent)]",
              ].join(" ")}
            >
              {respondedRef.current ? t("common.matched") : t("common.match")}
            </button>
          </>
        )}
        {subPhase === "gap" && <span className="text-5xl text-fg-3">+</span>}
        {subPhase === "summary" && lastResult && (
          <NBackSummary
            result={lastResult}
            currentN={n}
            onContinue={continueFromSummary}
          />
        )}
      </section>

      <p className="text-center text-fg-3 text-xs py-3">
        {t("nback.matchHint", { n })}
      </p>
    </main>
  );
}

function NBackSummary({
  result,
  currentN,
  onContinue,
}: {
  result: NBackScore;
  currentN: number;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const passed = result.accuracyPct >= 70;
  const nextN = adaptiveN(currentN, result.accuracyPct, 8);
  return (
    <div className="flex flex-col items-center gap-5">
      <span className={`text-3xl font-medium ${passed ? "text-green" : "text-red"}`}>
        {passed ? t("nback.sequenceComplete") : t("nback.sequenceFailed")}
      </span>
      <span className="text-fg-1 text-lg">
        {t("nback.accuracy", { pct: result.accuracyPct })}
      </span>
      <div className="flex gap-6 font-mono text-xs">
        <span><span className="text-green">{result.hits}</span> {t("nback.hits")}</span>
        <span><span className="text-red">{result.misses}</span> {t("nback.misses")}</span>
        <span><span className="text-amber">{result.falseAlarms}</span> {t("nback.falseAlarms")}</span>
      </div>
      {nextN !== currentN && (
        <span className={`text-sm ${nextN > currentN ? "text-green" : "text-amber"}`}>
          {t("nback.nLevelChange", { from: currentN, to: nextN })}
        </span>
      )}
      <button
        type="button"
        onClick={onContinue}
        className="px-7 py-2.5 rounded-full bg-cyan text-bg-1 text-sm font-semibold"
      >
        {t("common.continue")}
      </button>
    </div>
  );
}
